'use client'

import AirspaceAlertWrapper from '@/src/components/common/home/common/airspaceAlert'
import ErrorModal from '@/src/components/common/itinerary/errorModal/errorModal'
import LoaderImage from '@/src/components/common/loaderImage'
import EmptyFlightScreenWrapper from '@/src/components/common/srp/emptyFlightScreen'
import SrpErrorStateWrapper from '@/src/components/common/srp/errorState'
import AirlineFilterWrapperTopBar from '@/src/components/common/srp/filters/airlineFilterTopBar'
import NearbyAirportPopup from '@/src/components/common/srp/nearbyAirportPopup'
import StillAroundPopup from '@/src/components/common/srp/stillAroundPopup/StillAroundPopup'
import Statusbar from '@/src/components/common/ui/statusbar'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import CouponSectionWrapper from '@/src/components/desktop/srp/couponSection'
import FareFamilyWrapper from '@/src/components/desktop/srp/fareFamily'
import FullPageLoader from '@/src/components/mobile/fullPageLoader'
import MobileDateCarouselWrapper from '@/src/components/mobile/srp/dateCarousel/MobileDateCarouselWrapper'
import FlightDetailsWrapper from '@/src/components/mobile/srp/flightDetails'
import FlightResultsWrapper from '@/src/components/mobile/srp/flightResults'
import SrpLoadingShimmer from '@/src/components/mobile/srp/loadingShimmers/srpLoadingShimmer'
import RTSolutionFlowWrapperMobile from '@/src/components/mobile/srp/rtSolutionViewFlow'
import SrpFilterAndSortTabsAndDrawerWrapper from '@/src/components/mobile/srp/srpFilterAndSortTabsBar/srpFilterAndSortTabsAndDrawerWrapper'
import SrpHeaderWithSearchWrapper from '@/src/components/mobile/srp/srpSearchModifier'
import { MOBILE } from '@/src/constants/deviceTypes'
import { TravelClassType } from '@/src/constants/travelers'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import useNearByAirportAlertManager from '@/src/hooks/srp/nearbyAirportAlertManager'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSrpController from '@/src/hooks/srp/srpController'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getFormattedDate } from '@/src/utils/date'
import { onPageStart } from '@/src/utils/generalUtils/onPageStart'
import { useSetLanguageCookie } from '@/src/utils/language'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'

const SrpContainerMobile = () => {
    // Set selectedLanguage cookie on page load based on locale
    useSetLanguageCookie()

    useEffect(() => {
        onPageStart('FLIGHTS_SRP')
    }, [])

    const t = useTranslations()

    const {
        handleCreateBooking,
        handleFlightDetailsClose,
        onViewFlightDetails,
        selectedFlightForFlightDetails,
        isCreateItineraryLoading,
        errorCreateItinerary,
        setErrorCreateItinerary,
        handleRetryCreateBooking,
        selectedFlightForFareFamily,
        handleBookClick,
        rawFareFamilyData,
        isBookingInProgress,
        handleCloseFareFamily,
        showStillAroundPopup,
        handleReFetchWithSameQuery,
    } = useSrpController({ deviceType: MOBILE })

    const {
        srpResultsLoading,
        srpResultsError,

        invalidUrl,
        showEmptyFlightScreen,
        jsons,
        sectors,
        sid,
        isRT,
        isSolutionView
    } = useSearchResults()

    // Nearby airport alert manager - handles all nearby airport logic internally
    const {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen,
        popupData
    } = useNearByAirportAlertManager({
        onContinue: handleBookClick,
        jsons: jsons || null,
        sectors: sectors || null,
        handleFlightDetailsClose
    })

    const { urlQueryObj } = useGetSearchParamsObj()
    const {
        from,
        to,
        class: travelClass,
        adults,
        childs,
        infants,
        depart_date = '',
        return_date = ''
    } = urlQueryObj || {}
    const { formatDate } = useFormatDate()
    const formattedDepartDate = formatDate(getFormattedDate(depart_date, 'MM/DD/YYYY'), 'D MMM')
    const formattedReturnDate = !!return_date ? formatDate(getFormattedDate(return_date, 'MM/DD/YYYY'), 'D MMM') : ''
    const totalTravellers = Number(adults || '1') + Number(childs || '0') + Number(infants || '0')
    const travelers = `${totalTravellers} ${t('common.traveller', { s: totalTravellers > 1 ? 's' : '' })}`

    return (
        <Box
            sx={{
                bgcolor: '#FFFFFF',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <LoaderImage height={50} width={360} />
            <Toaster position="bottom-center" reverseOrder={false} gutter={8} />
            <ErrorBoundaryWrapper identifier="srp-header-with-search-wrapper-PWA">
                <SrpHeaderWithSearchWrapper
                    jsons={jsons}
                    from={from as string}
                    to={to as string}
                    departDate={formattedDepartDate}
                    returnDate={formattedReturnDate}
                    travelers={travelers}
                    travelClass={(travelClass || 'Economy') as TravelClassType}
                    deviceType={MOBILE}
                    srpResultsLoading={srpResultsLoading}
                />
            </ErrorBoundaryWrapper>

            {/* Sort Tabs Wrapper */}

            <Statusbar loading={srpResultsLoading} />
            <AirspaceAlertWrapper />
            {!!srpResultsError || invalidUrl ? (
                <ErrorBoundaryWrapper identifier="srp-error-state-wrapper-PWA">
                    <SrpErrorStateWrapper
                        pastDateError={invalidUrl === 'dateInPast'}
                        errorStatus={invalidUrl === 'requiredFieldsMissing' ? 400 : srpResultsError?.status || 500}
                    />
                </ErrorBoundaryWrapper>
            ) : srpResultsLoading ? (
                <ErrorBoundaryWrapper identifier="srp-loading-shimmer-PWA">
                    <SrpLoadingShimmer />
                </ErrorBoundaryWrapper>
            ) : (
                <>
                    {isRT && isSolutionView ? (
                        <ErrorBoundaryWrapper identifier="rt-solution-flow-wrapper-mobile-PWA">
                            <RTSolutionFlowWrapperMobile handleFlightBook={handleCreateBooking} />
                        </ErrorBoundaryWrapper>
                    ) : (
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', px: 2 }}>
                                <CouponSectionWrapper jsons={jsons} isRT={isRT} deviceType={MOBILE} />
                                <ErrorBoundaryWrapper identifier="mobile-date-carousel-wrapper-PWA">
                                    <MobileDateCarouselWrapper />
                                </ErrorBoundaryWrapper>
                                <ErrorBoundaryWrapper identifier="airline-filter-wrapper-top-bar-PWA">
                                    <AirlineFilterWrapperTopBar showArrows={false} deviceType={MOBILE} />
                                </ErrorBoundaryWrapper>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: '#F9FAFB' }}>
                                <ErrorBoundaryWrapper identifier="srp-filter-and-sort-tabs-and-drawer-wrapper-PWA">
                                    <SrpFilterAndSortTabsAndDrawerWrapper showEmptyFlightScreen={showEmptyFlightScreen}/>
                                </ErrorBoundaryWrapper>
                                {showEmptyFlightScreen ? (
                                    <ErrorBoundaryWrapper identifier="empty-flight-screen-wrapper-PWA">
                                        <EmptyFlightScreenWrapper deviceType={MOBILE} />
                                    </ErrorBoundaryWrapper>
                                ) : (
                                    <ErrorBoundaryWrapper identifier="OW-flight-results-wrapper-PWA">
                                        <FlightResultsWrapper
                                            onBookFlight={handleFlightSelection} // Use handleFlightSelection instead of handleBookClick
                                            onViewDetails={flight => onViewFlightDetails([flight])}
                                            onSortByPrice={() => { }}
                                        />
                                    </ErrorBoundaryWrapper>
                                )}
                            </Box>
                        </Box>
                    )}

                    {(isCreateItineraryLoading || isBookingInProgress) && (
                        <FullPageLoader
                            message={
                                <Box mt={3} textAlign="center">
                                    <Typography variant="h6" fontWeight={700} color="text.secondary">
                                        {isBookingInProgress && !isCreateItineraryLoading
                                            ? t('createItineraryStates.loadingFareOptions')
                                            : t('createItineraryStates.creatingItinerary')}
                                    </Typography>
                                </Box>
                            }
                        />
                    )}

                    <SwipeableDrawerWithBackNavigation
                        open={!!selectedFlightForFlightDetails}
                        onClose={handleFlightDetailsClose}
                        anchor="bottom"
                    >
                        {selectedFlightForFlightDetails ? (
                            <ErrorBoundaryWrapper identifier="flight-details-wrapper-PWA">
                                <FlightDetailsWrapper
                                    flights={selectedFlightForFlightDetails}
                                    sectors={sectors!}
                                    jsons={jsons!}
                                    onSelect={() => handleFlightSelection(selectedFlightForFlightDetails[0])} // Use handleFlightSelection here too
                                    sid={sid!}
                                    showBaggage={true}
                                    isRTSolutionView={false}
                                />
                            </ErrorBoundaryWrapper>
                        ) : null}
                    </SwipeableDrawerWithBackNavigation>

                    <NearbyAirportPopup
                        open={isOpen}
                        onClose={handleNearbyPopupClose}
                        onContinue={handleNearbyPopupContinue}
                        data={popupData}
                    />

                    {errorCreateItinerary && (
                        <ErrorModal
                            isOpen={true}
                            primaryError={t('createItineraryStates.serverError')}
                            secondaryError={t('createItineraryStates.errorMessage')}
                            primaryButtonText={t('common.retry')}
                            secondaryButtonText={t('common.close')}
                            primaryButtonAction={handleRetryCreateBooking}
                            secondaryButtonAction={() => setErrorCreateItinerary?.(null)}
                        />
                    )}
                    <SwipeableDrawerWithBackNavigation
                        open={!!selectedFlightForFareFamily}
                        onClose={handleCloseFareFamily}
                        anchor="bottom"
                        keepMounted
                    >
                        <ErrorBoundaryWrapper identifier="fare-family-wrapper-PWA">
                            <FareFamilyWrapper
                                flight={selectedFlightForFareFamily ?? undefined}
                                sectors={sectors!}
                                handleClose={handleCloseFareFamily}
                                handleCreateBooking={(flight, plan) => handleCreateBooking([flight], [plan])}
                                jsons={jsons!}
                                desktop={false}
                                rawFareFamilyData={rawFareFamilyData!}
                            />
                        </ErrorBoundaryWrapper>
                    </SwipeableDrawerWithBackNavigation>
                </>
            )}

            {showStillAroundPopup && <StillAroundPopup handleReFetchWithSameQuery={handleReFetchWithSameQuery} />}
        </Box>
    )
}

export default SrpContainerMobile
